import { sitesApi } from "#/api/sitesApi";
import { showSuccessNotification } from "#/components/notifications";
import { type CreateSitePayload } from "#/types/sites";
import { Button, Modal, TextInput, type ModalProps } from "@mantine/core";
import { useForm } from "@mantine/form";
import isEqual from "lodash.isequal";
import { useEffect, useState } from "react";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useTranslation } from "react-i18next";

export interface Props extends ModalProps {
  onSuccess: () => void;
}

const CreateSiteModal = ({ onSuccess, ...props }: Props) => {
  const { canWrite } = usePermission();
  const { t } = useTranslation();
  const [generating, setGenerating] = useState<boolean>(false);

  const emptyForm = {
    name: "",
    location_text: "",
  };

  const form = useForm<CreateSitePayload>({
    initialValues: emptyForm,
    validate: {
      name: (value) => (value ? null : t("sites.validation.nameRequired")),
      location_text: (value) => (value ? null : t("sites.validation.locationRequired")),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setGenerating(true);
    try {
      const res = await sitesApi.create({
        ...values,
      });
      setGenerating(false);
      onSuccess();
      showSuccessNotification({
        message: res.message ?? t("sites.siteCreatedSuccess"),
      });
      form.reset();
    } catch (error) {
      console.error("🚀 ~ handleSubmit ~ error:", error);
      setGenerating(false);
    }
  };

  function hasFormUpdate(): boolean {
    return !isEqual(form.values, emptyForm);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!props.opened && !isEqual(form.values, emptyForm)) {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.opened]);

  return (
    <Modal
      {...props}
      title={t("sites.createNewSite")}
      centered
      closeOnClickOutside={false}
      onClose={() => {
        form.reset();
        props.onClose();
      }}
      classNames={{
        title: "font-bold",
      }}
    >
      <form
        className="space-y-4 relative"
        onSubmit={form.onSubmit(handleSubmit)}
      >
        <div className="w-full flex flex-col gap-4">
          <TextInput
            label={t("common.name")}
            withAsterisk
            placeholder={t("sites.enterSiteName")}
            {...form.getInputProps("name")}
          />

          <TextInput
            label={t("common.location")}
            withAsterisk
            placeholder={t("sites.enterSiteLocation")}
            {...form.getInputProps("location_text")}
          />
        </div>

        <div className="w-full grid grid-cols-2 gap-4 mt-4">
          <Button
            color="black"
            variant="outline"
            type="reset"
            onClick={() => {
              form.reset();
            }}
            size="md"
            disabled={!hasFormUpdate()}
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={generating}
              type="submit"
              size="md"
              disabled={!hasFormUpdate() || !canWrite()}
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      </form>
    </Modal>
  );
};

export default CreateSiteModal;
